import { useEffect, useRef } from 'react';
import {
  isConnectorAvailable,
  onProxyFetchFailed,
  onProxyStreamComplete,
  onProxyStreamRequest,
} from '../lib/connector';
import {
  handleProxyFetchFailedEvent,
  markProxyStreamComplete,
  markProxyStreamRequest,
} from '../utils/proxyFetchFailedUI';

function bindDelivery(payload) {
  return [
    payload?.renderer_ip,
    payload?.renderer_name,
    payload?.renderer_id,
  ];
}

/**
 * Subscribes to Connector `proxy_fetch_failed` and surfaces firewall guidance via toast.
 * Also latches Proxy stream request/complete so isolation banners cannot fire during delivery.
 * Mount once in AppLayout (Connector desktop only).
 */
export default function ProxyFetchFailedPrompt() {
  const unlistenRef = useRef([]);
  const subscribedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    const subscribe = async () => {
      if (cancelled || subscribedRef.current || !isConnectorAvailable()) return;
      try {
        const unlistens = [];
        unlistens.push(await onProxyFetchFailed((payload) => {
          handleProxyFetchFailedEvent(payload);
        }));
        try {
          unlistens.push(await onProxyStreamRequest((payload) => {
            markProxyStreamRequest(...bindDelivery(payload));
          }));
          unlistens.push(await onProxyStreamComplete((payload) => {
            markProxyStreamComplete(...bindDelivery(payload));
          }));
        } catch {
          /* older connectors omit stream request/complete events */
        }
        if (cancelled) {
          for (const unlisten of unlistens) {
            if (typeof unlisten === 'function') unlisten();
          }
          return;
        }
        unlistenRef.current = unlistens;
        subscribedRef.current = true;
      } catch (err) {
        console.error('[ProxyFetchFailed] listen failed:', err);
      }
    };

    void subscribe();

    // Connector IPC may not be ready on first paint — retry briefly.
    if (!subscribedRef.current) {
      let attempts = 0;
      pollTimer = window.setInterval(() => {
        attempts += 1;
        if (subscribedRef.current || attempts > 15) {
          window.clearInterval(pollTimer);
          return;
        }
        void subscribe();
      }, 2000);
    }

    return () => {
      cancelled = true;
      if (pollTimer) window.clearInterval(pollTimer);
      for (const unlisten of unlistenRef.current) {
        if (typeof unlisten === 'function') unlisten();
      }
      subscribedRef.current = false;
      unlistenRef.current = [];
    };
  }, []);

  return null;
}
